<template>
  <div id="app">
    <header v-if="shouldShowHeader">
      <nav class="navbar">
        <div class="nav-brand">
          <div class="brand-logo">
            <h1>Diva AI</h1>
          </div>
        </div>
        <div class="nav-links">
          <LanguageSelector mode="dark" />
          <router-link to="/" class="nav-link">{{ t('nav.home') }}</router-link>
          <!-- <router-link to="/about" class="nav-link">关于</router-link> -->

          <!-- 用户认证相关导航 -->
          <div v-if="!authStore.isAuthenticated" class="auth-links">
            <router-link to="/auth" class="nav-link nav-link--auth">{{ t('nav.login_register') }}</router-link>
          </div>

          <!-- 已登录用户导航 -->
          <div v-else class="user-menu">
            <div ref="userInfoRef" class="user-info" @click="toggleUserMenu">
              <div class="user-avatar">
                <img :src="authStore.getUserAvatarUrl()" :alt="authStore.getUserDisplayName()" />
              </div>
              <span class="user-name">{{ authStore.getUserDisplayName() }}</span>
              <svg viewBox="0 0 24 24" width="16" height="16" class="dropdown-icon">
                <path fill="currentColor" d="M7 10l5 5 5-5z" />
              </svg>
            </div>

            <Teleport to="body">
              <div v-if="showUserMenu" class="user-dropdown" :style="userDropdownStyle" @click.stop>
                <div class="user-dropdown-header">
                  <div class="user-details">
                    <div class="user-name">{{ authStore.user?.name }}</div>
                    <div class="user-role">{{ authStore.userRoleName }}</div>
                    <div class="user-voice">{{ authStore.userVoiceTypeName }}</div>
                  </div>
                </div>

                <div class="user-dropdown-menu">
                  <router-link to="/dashboard" class="dropdown-item" @click="closeUserMenu">
                    <svg viewBox="0 0 24 24" width="16" height="16">
                      <path fill="currentColor" d="M3 13h8V3H3v10zm0 8h8v-6H3v6zm10 0h8V11h-8v10zm0-18v6h8V3h-8z" />
                    </svg>
                    {{ t('nav.console') }}
                  </router-link>

                  <router-link to="/settings/profile" class="dropdown-item" @click="closeUserMenu">
                    <svg viewBox="0 0 24 24" width="16" height="16">
                      <path fill="currentColor"
                        d="M12 12c2.21 0 4-1.79 4-4s-1.79-4-4-4-4 1.79-4 4 1.79 4 4 4zm0 2c-2.67 0-8 1.34-8 4v2h16v-2c0-2.66-5.33-4-8-4z" />
                    </svg>
                    {{ t('nav.profile') }}
                  </router-link>

                  <div class="dropdown-divider"></div>

                  <button class="dropdown-item dropdown-item--logout" @click="handleLogout">
                    <svg viewBox="0 0 24 24" width="16" height="16">
                      <path fill="currentColor"
                        d="M17 7l-1.41 1.41L18.17 11H8v2h10.17l-2.58 2.59L17 17l5-5zM4 5h8V3H4c-1.1 0-2 .9-2 2v14c0 1.1.9 2 2 2h8v-2H4V5z" />
                    </svg>
                    {{ t('auth.logout') }}
                  </button>
                </div>
              </div>
            </Teleport>
          </div>
        </div>
      </nav>
    </header>

    <main class="main-content" :class="{ 'no-header': !shouldShowHeader }">
      <router-view />
    </main>

    <footer v-if="shouldShowHeader" class="footer">
      <div class="footer-content">
        <p>&copy; 2025 Diva AI. All rights reserved.</p>
      </div>
    </footer>

    <!-- 全局通知组件 -->
    <BaseNotification />
    
    <!-- 全局悬浮按钮 -->
    <GuidingButton @click="handleFloatingButtonClick" />
  </div>
</template>

<script>
import { BaseNotification } from '@/components/base';
import LanguageSelector from '@/components/common/LanguageSelector.vue';
import GuidingButton from '@/components/common/GuidingButton.vue';
import { useAuthStore } from '@/stores/auth.js';
import { computed, onMounted, onUnmounted, ref, nextTick } from 'vue';
import { useRoute } from 'vue-router';
import { useI18n } from 'vue-i18n';

export default {
  name: 'App',
  components: {
    BaseNotification,
    LanguageSelector,
    GuidingButton
  },
  setup() {
    const { t } = useI18n();
    const authStore = useAuthStore();
    const route = useRoute();
    const showUserMenu = ref(false);
    const userInfoRef = ref(null);
    const userDropdownStyle = ref({});

    // 判断是否应该显示header（只在home和about页面显示）
    const shouldShowHeader = computed(() => {
      const routesWithHeader = ['/', '/about'];
      return routesWithHeader.includes(route.path) || route.name === 'home' || route.name === 'about';
    });

    // 更新用户下拉菜单位置
    const updateUserDropdownPosition = () => {
      if (userInfoRef.value) {
        const rect = userInfoRef.value.getBoundingClientRect();
        userDropdownStyle.value = {
          position: 'fixed',
          top: `${rect.bottom + 8}px`,
          right: `${window.innerWidth - rect.right}px`,
          zIndex: 2500
        };
      }
    };

    // 切换用户菜单
    const toggleUserMenu = async () => {
      showUserMenu.value = !showUserMenu.value;
      if (showUserMenu.value) {
        await nextTick();
        updateUserDropdownPosition();
      }
    };

    // 关闭用户菜单
    const closeUserMenu = () => {
      showUserMenu.value = false;
    };

    // 处理登出
    const handleLogout = () => {
      authStore.logout();
      closeUserMenu();
      window.$notify?.success('已安全退出登录');
    };

    // 点击外部关闭菜单
    const handleClickOutside = (event) => {
      if (!event.target.closest('.user-menu')) {
        closeUserMenu();
      }
    };

    onMounted(() => {
      document.addEventListener('click', handleClickOutside);
    });

    onUnmounted(() => {
      document.removeEventListener('click', handleClickOutside);
    });

    // 处理悬浮按钮点击
    const handleFloatingButtonClick = (event) => {
      // 防止事件冒泡和重复触发
      if (event) {
        event.preventDefault();
        event.stopPropagation();
      }
      
      // 防抖处理，避免快速重复点击
      if (handleFloatingButtonClick.isProcessing) {
        return;
      }
      
      handleFloatingButtonClick.isProcessing = true;
      
      // 这里可以添加悬浮按钮的具体功能
      // 比如打开帮助面板、显示使用向导等
      console.log('悬浮按钮被点击 - App.vue');
      
      // 重置防抖标志
      setTimeout(() => {
        handleFloatingButtonClick.isProcessing = false;
      }, 500);
    };

    return {
      t,
      authStore,
      showUserMenu,
      shouldShowHeader,
      toggleUserMenu,
      closeUserMenu,
      handleLogout,
      handleFloatingButtonClick,
      userInfoRef,
      userDropdownStyle
    };
  }
}
</script>

<style scoped>
#app {
  min-height: 100vh;
  display: flex;
  flex-direction: column;
}

.navbar {
  background-color: transparent;
  border-bottom: none;
  padding: var(--spacing-md) var(--spacing-xl);
  display: flex;
  justify-content: space-between;
  align-items: center;
  position: sticky;
  top: 0;
  z-index: 100;
  backdrop-filter: blur(8px);
}

.nav-brand {
  display: flex;
  align-items: center;
}

.brand-logo {
  display: flex;
  align-items: center;
  gap: var(--spacing-sm);
}

.logo-icon {
  color: var(--color-primary);
}

.nav-brand h1 {
  margin: 0;
  font-size: 1.5rem;
  font-weight: 500;
  color: white;
  text-shadow: 1px 1px 2px rgba(0, 0, 0, 0.7);
}

.nav-links {
  display: flex;
  align-items: center;
  gap: var(--spacing-md);
}

.auth-links {
  display: flex;
  gap: var(--spacing-xs);
}

.nav-link {
  color: rgba(255, 255, 255, 0.9);
  text-decoration: none;
  padding: var(--spacing-sm) var(--spacing-md);
  border-radius: var(--border-radius-large);
  transition: all 0.2s ease;
  font-weight: 500;
  font-size: 14px;
  text-shadow: 1px 1px 2px rgba(0, 0, 0, 0.7);
}

.nav-link:hover {
  background-color: rgba(255, 255, 255, 0.1);
  color: white;
}

.nav-link.router-link-active {
  background-color: rgba(255, 255, 255, 0.2);
  color: white;
}

.nav-link--auth {
  background-color: var(--color-primary);
  color: white;
  font-weight: 500;
}

.nav-link--auth:hover {
  background-color: var(--color-primary-hover);
  color: white;
}

/* 用户菜单样式 */
.user-menu {
  position: relative;
}

.user-info {
  display: flex;
  align-items: center;
  gap: var(--spacing-sm);
  padding: var(--spacing-sm) var(--spacing-md);
  border-radius: var(--border-radius-large);
  cursor: pointer;
  transition: all 0.2s ease;
}

.user-info:hover {
  background-color: var(--color-surface-variant);

  .user-name {
    color: var(--color-on-surface);
    transition: all 0.2s ease;
  }
}

.user-avatar {
  width: 32px;
  height: 32px;
  border-radius: 50%;
  overflow: hidden;
  background-color: var(--color-primary-light);
  display: flex;
  align-items: center;
  justify-content: center;
}

.user-avatar img {
  width: 100%;
  height: 100%;
  object-fit: cover;
}

.user-name {
  font-size: 14px;
  font-weight: 500;
  color: var(--color-surface-variant);
}

.dropdown-icon {
  color: var(--color-on-surface-secondary);
  transition: transform 0.2s ease;
}

.user-info:hover .dropdown-icon {
  transform: rotate(180deg);
}

.user-dropdown {
  background-color: var(--color-surface);
  border: 1px solid var(--color-outline-variant);
  border-radius: var(--border-radius-large);
  box-shadow: var(--shadow-3);
  min-width: 240px;
  overflow: hidden;
}

.user-dropdown-header {
  padding: var(--spacing-md);
  background-color: var(--color-surface-variant);
  border-bottom: 1px solid var(--color-outline-variant);
}

.user-details .user-name {
  font-size: 16px;
  font-weight: 500;
  color: var(--color-on-surface);
  margin-bottom: var(--spacing-xs);
}

.user-role,
.user-voice {
  font-size: 12px;
  color: var(--color-on-surface-secondary);
  margin-bottom: 2px;
}

.user-dropdown-menu {
  padding: var(--spacing-sm) 0;
}

.dropdown-item {
  display: flex;
  align-items: center;
  gap: var(--spacing-sm);
  padding: var(--spacing-sm) var(--spacing-md);
  color: var(--color-on-surface);
  text-decoration: none;
  font-size: 14px;
  transition: all 0.2s ease;
  border: none;
  background: none;
  width: 100%;
  text-align: left;
  cursor: pointer;
}

.dropdown-item:hover {
  background-color: var(--color-surface-variant);
}

.dropdown-item--logout {
  color: var(--color-error);
}

.dropdown-item--logout:hover {
  background-color: var(--color-error);
  color: white;
}

.dropdown-divider {
  height: 1px;
  background-color: var(--color-outline-variant);
  margin: var(--spacing-sm) 0;
}

.main-content {
  flex: 1;
  padding: var(--spacing-xl);
  max-width: 1200px;
  margin: 0 auto;
  width: 100%;
}

.main-content.no-header {
  padding: 0;
  max-width: none;
  margin: 0;
  width: 100%;
  height: 100vh;
}

.footer {
  background-color: var(--color-surface-variant);
  border-top: 1px solid var(--color-outline-variant);
  padding: var(--spacing-xl);
  margin-top: auto;
}

.footer-content {
  max-width: 1200px;
  margin: 0 auto;
  text-align: center;
}

.footer-content p {
  margin: 0;
  color: var(--color-on-surface-variant);
  font-size: 14px;
}

.footer-subtitle {
  margin-top: var(--spacing-xs) !important;
  font-size: 12px !important;
  color: var(--color-on-surface-secondary) !important;
}

/* 响应式设计 */
@media (max-width: 768px) {
  .navbar {
    padding: var(--spacing-md);
    flex-direction: column;
    gap: var(--spacing-md);
  }

  .nav-links {
    width: 100%;
    justify-content: center;
  }

  .main-content {
    padding: var(--spacing-md);
  }

  .footer {
    padding: var(--spacing-md);
  }
}
</style>
